// src/components/layout/Sidebar.js
import React, { useEffect, useState } from 'react';
import LogoMark from '../LogoMark';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../../store';
import { Avatar } from '../../pages/Profile';

const NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: '⊞', path: '/dashboard' },
  {
    id: 'posts', label: 'Posts', icon: '≡', path: '/posts',
    children: [
      { label: 'Create Post', path: '/posts/create' },
      { label: 'Manage Posts', path: '/posts/manage', badge: null },
      { label: 'Calendar', path: '/posts/calendar' },
      { label: 'Bulk Import', path: '/posts/bulk-import' },
      { label: 'Pending Review', path: '/posts/pending-review', badge: '0' },
      { label: 'Drafts', path: '/posts/drafts', badge: null },
    ]
  },
  { id: 'calendar', label: 'Calendar', icon: '📅', path: '/posts/calendar' },
  {
    id: 'accounts', label: 'Accounts', icon: '⊕', path: '/accounts',
    children: [
      { label: 'Connect Account', path: '/accounts/connect' },
      { label: 'Manage Accounts', path: '/accounts' },
    ]
  },
  {
    id: 'groups', label: 'Groups', icon: '◎', path: '/groups',
    children: [
      { label: 'Create Group', path: '/groups/create' },
      { label: 'Manage Groups', path: '/groups' },
    ]
  },
  { id: 'analytics', label: 'Analytics', icon: '📊', path: '/analytics' },
  { id: 'team', label: 'Team', icon: '👥', path: '/settings/team' },
];

// Shown to the superadmin only (App.js guards the routes too).
const ADMIN_NAV = {
  id: 'admin', label: 'Admin', icon: '🛡', path: '/admin',
  children: [
    { label: 'Overview', path: '/admin' },
    { label: 'Organizations', path: '/admin/organizations' },
    { label: 'Users', path: '/admin/users' },
    { label: 'Activity Log', path: '/admin/activity' },
  ]
};

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useStore();
  const { postsSummary, adminPending, fetchAdminPending } = useStore();
  const isSuperadmin = user?.role === 'superadmin';
  const [openMenus, setOpenMenus] = useState(() => ({ admin: location.pathname.startsWith('/admin') }));
  const nav = isSuperadmin ? [...NAV, ADMIN_NAV] : NAV;

  useEffect(() => { if (isSuperadmin) fetchAdminPending(); }, [isSuperadmin, location.pathname, fetchAdminPending]);

  const toggle = (id) => setOpenMenus(prev => ({ ...prev, [id]: !prev[id] }));

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const getBadge = (label) => {
    if (label === 'Manage Posts') return postsSummary?.queued || null;
    if (label === 'Drafts') return postsSummary?.drafts || null;
    if (label === 'Pending Review') return postsSummary?.pending_review || '0';
    if (label === 'Organizations') return adminPending || null;
    return null;
  };

  return (
    <div className="sidebar">
      <div className="sb-brand">
        <LogoMark size={28} />
        <div className="label" style={{ minWidth: 0 }}>
          <div className="sb-brand-name">Post Calendar</div>
          {user?.organization_name && (
            <div style={{ fontSize: 10, color: '#6b7a99', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={user.organization_name}>{user.organization_name}</div>
          )}
        </div>
      </div>

      <nav className="sb-nav">
        {nav.map(item => (
          <div key={item.id}>
            <div
              className={`sb-link ${isActive(item.path) ? 'active' : ''} ${openMenus[item.id] ? 'open' : ''}`}
              onClick={() => {
                if (item.children) toggle(item.id);
                else navigate(item.path);
              }}
            >
              <span style={{ fontSize: 17, minWidth: 20 }}>{item.icon}</span>
              <span className="label">{item.label}</span>
              {item.children && <span className="arrow label">›</span>}
            </div>
            {item.children && (
              <div className={`sb-submenu ${openMenus[item.id] ? 'open' : ''}`}>
                {item.children.map((sub, i) => {
                  const badge = sub.badge !== undefined ? sub.badge : getBadge(sub.label);
                  return (
                    <div
                      key={i}
                      className={`sb-sub-item ${location.pathname === sub.path ? 'active' : ''}`}
                      onClick={() => navigate(sub.path)}
                    >
                      <span>{sub.label}</span>
                      {badge !== null && badge !== undefined && (
                        <span className="sb-sub-badge">{badge}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </nav>

      <div className="sb-sep" />
      <div className="sb-bottom">
        <div className="sb-user">
          <div
            className={`sb-user-link ${location.pathname === '/settings/profile' ? 'active' : ''}`}
            onClick={() => navigate('/settings/profile')}
            title="Profile settings"
          >
            <Avatar user={user} />
            <div className="sb-user-info label">
              <div style={{ fontSize: 12, fontWeight: 600, color: '#c8d0e0', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</div>
              <div style={{ fontSize: 10, color: '#6b7a99', textTransform: 'capitalize' }}>{isSuperadmin ? 'Superadmin' : user?.org_role || 'member'}</div>
            </div>
          </div>
          <span
            onClick={() => logout()}
            style={{ marginLeft: 'auto', color: '#6b7a99', cursor: 'pointer', fontSize: 16 }}
            title="Logout"
          >⏻</span>
          <span
            onClick={() => { if (window.confirm('Sign out of Post Calendar on all devices?')) logout(true); }}
            style={{ marginLeft: 8, color: '#6b7a99', cursor: 'pointer', fontSize: 14 }}
            title="Log out of all devices"
          >⎋</span>
        </div>
      </div>
    </div>
  );
}
